import { INTENT_LIMITS, validatePremiumOrder, type BrokerConnectionDto, type OrderSideValue, type PremiumOrderRequest } from '@apna/shared'
import type { ClickLots } from './quantity'
import type { ClickOrder } from './placeOrder'

export interface ChainLimitSelection {
  strike: number
  legs: { token: number; optionType: 'CE' | 'PE' }[]
}
export interface ChainLimitTicket extends ChainLimitSelection {
  requestId: string
  connection: BrokerConnectionDto
  sessionId: string | null
  underlying: string
  expiry: string
  lotSize: number
  tickSize: number
  /**
   * How many lots this ticket sends for a side.
   *
   * The screen's size control answers this, and in ₹ mode the answer is not
   * a constant: a BUY is divided out of the budget by premium, a SELL by the
   * margin it blocks. The ticket asks again whenever the side changes.
   */
  lotsFor: (side: OrderSideValue) => ClickLots
  quote: number
}

export function limitTicketOrder(ticket: ChainLimitTicket, side: OrderSideValue, lots: number, premium: PremiumOrderRequest): { ok: true; order: ClickOrder } | { ok: false; reason: string } {
  const invalid = validatePremiumOrder(premium, side)
  if (invalid) return { ok: false, reason: invalid }
  if (!Number.isInteger(lots) || lots < 1 || lots > INTENT_LIMITS.maxLots) return { ok: false, reason: 'Set a valid number of lots.' }
  if (ticket.legs.length < 1 || ticket.legs.length > 2 || ticket.legs.some((l) => !(l.token > 0))) return { ok: false, reason: 'The selected contracts are unavailable.' }
  for (const price of [premium.limitPrice, premium.stopLoss, premium.target]) {
    if (price != null && Math.abs(price / ticket.tickSize - Math.round(price / ticket.tickSize)) > 1e-6) return { ok: false, reason: `Prices must follow the ${ticket.tickSize} tick size.` }
  }
  return { ok: true, order: {
    clientRequestId: ticket.requestId, premiumOrder: premium,
    label: `${side} LIMIT ${lots} × ${ticket.strike} ${ticket.legs.length === 2 ? 'CE+PE' : ticket.legs[0]!.optionType} @ ${premium.limitPrice.toFixed(2)}`,
    legs: ticket.legs.map((l) => ({ token: l.token, side, lots })),
  } }
}

/** ERROR refuses the send; WARN is said out loud and sends anyway. */
export type PremiumAdviceLevel = 'ERROR' | 'WARN'

export interface PremiumAdvice {
  level: PremiumAdviceLevel
  code: 'MARKETABLE' | 'STOP_LOSS_WRONG_SIDE' | 'TARGET_WRONG_SIDE'
  message: string
}

/**
 * Pre-trade sanity on the levels, judged against the LIVE premium.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THIS EXISTS SEPARATELY FROM `validatePremiumOrder`
 *
 * `validatePremiumOrder` (shared, and re-run on the server) compares the stop
 * and the target to the ENTRY LIMIT, which is an invariant: a stop above the
 * price you are willing to pay is incoherent whatever the market is doing, so
 * it is refused at the boundary and cannot be sent by any client.
 *
 * This function compares them to the LIVE PREMIUM, which is a different
 * question and cannot live in that boundary because the server's boundary has
 * no tick in hand. It is the check Qbar's MetaTrader path calls `checkStops`
 * (`metatrader.orderSpec.ts`), and it is there for the reason stated in that
 * file: a protective level on the wrong side of the market is one of the few
 * mistakes a broker will sometimes ACCEPT, closing the position instantly at a
 * loss. Our stop is app-managed rather than broker-managed, which makes it
 * worse, not better — the engine evaluates the condition on the next tick, so
 * a stop already through the market fires on the tick after the fill.
 *
 * BOTH references must agree. A buy whose stop is below the limit but above
 * the live premium is still a stop that triggers on arrival, so each level is
 * checked against the limit (there) and the market (here), and either one
 * being wrong refuses it.
 *
 * NO LIVE PREMIUM, NO VERDICT. When no tick has landed this returns nothing
 * rather than falling back to the quote captured when the ticket opened.
 * Blocking an order on a stale price, or clearing one, are both answers we
 * cannot stand behind; the limit-price invariant still applies either way.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function premiumAdvice(input: {
  side: OrderSideValue
  limitPrice: number | null
  stopLoss: number | null
  target: number | null
  /** Live combined premium. Null when any leg is unmarked. */
  ltp: number | null
  combined: boolean
}): PremiumAdvice[] {
  const { ltp } = input
  if (ltp === null || !Number.isFinite(ltp) || ltp <= 0) return []
  const buy = input.side === 'BUY'
  const market = `${input.combined ? 'combined premium' : 'premium'} of ₹${ltp.toFixed(2)}`
  const out: PremiumAdvice[] = []
  const live = (price: number | null): price is number => price !== null && Number.isFinite(price) && price > 0

  /*
   * MARKETABLE, NOT INVALID. A buy limit at or above the offer is a perfectly
   * legitimate order and traders place it deliberately to cross the spread —
   * it just is not the resting order the word "limit" suggests, so it is said
   * out loud and sent.
   */
  if (live(input.limitPrice) && (buy ? input.limitPrice >= ltp : input.limitPrice <= ltp)) {
    out.push({
      level: 'WARN',
      code: 'MARKETABLE',
      message: `This will not rest. A ${buy ? 'buy' : 'sell'} limit of ₹${input.limitPrice.toFixed(2)} is ${buy ? 'at or above' : 'at or below'} the live ${market}, so it trades on arrival like a market order — at whatever the book shows, never worse than your limit.`,
    })
  }

  if (live(input.stopLoss) && (buy ? input.stopLoss >= ltp : input.stopLoss <= ltp)) {
    out.push({
      level: 'ERROR',
      code: 'STOP_LOSS_WRONG_SIDE',
      message: `A stop loss on a ${buy ? 'buy' : 'sell'} must sit ${buy ? 'below' : 'above'} the live ${market}. At ₹${input.stopLoss.toFixed(2)} it is already through the market and would close the position on the first tick after it fills.`,
    })
  }

  if (live(input.target) && (buy ? input.target <= ltp : input.target >= ltp)) {
    out.push({
      level: 'ERROR',
      code: 'TARGET_WRONG_SIDE',
      message: `A target on a ${buy ? 'buy' : 'sell'} must sit ${buy ? 'above' : 'below'} the live ${market}. At ₹${input.target.toFixed(2)} it is already through the market and would close the position on the first tick after it fills.`,
    })
  }

  return out
}
